import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b7d7a464"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "card-global space-y-8" }
const _hoisted_2 = { class: "flex items-center gap-4" }
const _hoisted_3 = { class: "page-title flex-1 text-gray-800" }
const _hoisted_4 = {
  key: 0,
  class: "document-details-actions flex justify-between items-start gap-4"
}
const _hoisted_5 = { class: "document-details-actions__primary flex flex-wrap gap-2" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "document-details-actions__secondary flex flex-wrap justify-end gap-2 ml-auto" }
const _hoisted_8 = ["disabled"]

import BackButton from "@/components/common/BackButton.vue";
import { ref, reactive, computed, onMounted, watchEffect, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { TabButtons } from "@/components/common";
import {
  MainDocumentTable,
  AttachmentTable,
  ProcessHistory,
} from "@/components/documents/";
import { Document, ResponseData } from "@/types";
import { DocumentDataService } from "@/services";
import InfoList from "@/components/documents/InfoList.vue";
import DocumentHistory from "@/components/documents/DocumentHistory.vue";
import { DocumentStatusesEnum } from "@/store/modules/document/state";
import DocumentAccessTable from "@/components/documents/DocumentAccessTable.vue";
import { DocumentAccess } from "@/types/documents/document/DocumentAccess";
import DocumentFiles from "@/types/documents/document/DocumentFiles";
import LinkedDocuments from "@/components/documents/LinkedDocuments.vue";
import { t } from "@/i18n";
import { useEsedoDictionaries } from "@/composables/useEsedoDictionaries";
import { AuthGetters } from "@/store/modules/auth";
import EnbekDataService from "@/services/enbek/EnbekDataService";
import { EnbekEligibility } from "@/types/enbek/Enbek";
import EnbekDocumentPanel from "@/components/enbek/EnbekDocumentPanel.vue";

interface FileInfo {
  belongsToDocument: boolean;
  createdAt: string;
  ext: string;
  guid: string;
  name: string;
  size: number;
  type: string;
  updatedAt: string;
}

interface ElectronicCopyFile {
  type: string;
  originalFileGuid: string;
  electronicFileGuid: string;
  fileName: string;
}

interface DocumentFileInfo {
  id: string;
  name: string;
}

export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const buildDisplayFileName = (item: FileInfo) => `${item.name}${item.ext || ""}`;

const route = useRoute();
const router = useRouter();
const store = useStore();
const {
  loadAll: loadEsedoDictionaries,
  resolveDocumentTypeName,
  resolveQuestionNatureName,
  resolvePartnerNames,
} = useEsedoDictionaries();
const mainGuid = ref();
const mainFileInfo = ref();
const documentData = ref<Document>();
const events = ref({});
const attachedDocuments = ref<Array<FileInfo>>([]);
const electronicCopyByOriginalGuid = ref<Record<string, ElectronicCopyFile>>({});

const updateDocument = async () => {
  await router.push({
    name: "DocumentEdit",
    params: { id: documentData.value.id },
  });
};

const deleteDocument = async () => {
  await DocumentDataService.delete(documentData.value.id).then(
    async (response) => {
      await router.push({ name: "DocumentList" });
    }
  );
};

const sendDocumentForExecution = async () => {
  await DocumentDataService.sendForExecution(documentData.value.id).then(
    (response) => {
      router.go(0);
    }
  );
};

const returnDocument = async () => {
  await DocumentDataService.returnDocument(documentData.value.id).then(
    (response) => {
      router.go(0);
    }
  );
};

const documentFiles = computed(() => {
  const files: DocumentFileInfo[] = [];
  documentData.value?.documentFiles.forEach(function (file) {
    if (file.type === "ARCHIVE") {
      return;
    }

    const fileData = attachedDocuments.value.find(
      (item) => item.guid === file.fileGuid
    );

    if (!fileData) {
      return;
    }

    files.push({
      id: file.id!,
      name: fileData.name,
    });
  });

  return files;
});
const documentIsSigning = ref(false);
const currentUser = computed(() => store.getters[AuthGetters.GET_USER_IDENTITY]);
const hasEnbekPermission = computed(() =>
  Array.isArray(currentUser.value?.permissions) &&
  currentUser.value.permissions.includes("ENBEK_MANAGE")
);
const enbekEligibility = ref<EnbekEligibility | null>(null);
const canRegisterInEnbek = computed(() =>
  hasEnbekPermission.value &&
  enbekEligibility.value?.eligible === true &&
  enbekEligibility.value?.registrationExists !== true
);
const canCreateLinkedDocument = computed(
  () =>
    documentData.value?.status === DocumentStatusesEnum.SUCCESS &&
    documentData.value?.creatorInfo?.userInfo?.id === currentUser.value?.id
);

const documentDetails = computed(() => {
  const info = documentData.value?.documentInfo;
  const hasEsedo =
    info?.esedoDocumentTypeId != null ||
    info?.esedoQuestionNatureId != null ||
    (info?.counterpartyIds?.length ?? 0) > 0;

  return {
    regNumber: info?.regNumber || "",
    categoryName: documentData.value?.constructorInfo.name,
    createdBy: documentData.value?.creatorInfo?.userInfo.fullName,
    theme: info?.title,
    index: info?.nomenclatureInfo?.name,
    createdAt: documentData.value?.creatorInfo.createdAt,
    status: documentData.value?.status,
    esedoDocumentType: hasEsedo
      ? resolveDocumentTypeName(info?.esedoDocumentTypeId)
      : undefined,
    esedoQuestionNature: hasEsedo
      ? resolveQuestionNatureName(info?.esedoQuestionNatureId)
      : undefined,
    esedoPartners: hasEsedo
      ? resolvePartnerNames(info?.counterpartyIds)
      : undefined,
  };
});

const mainDocumentGuid = () => {
  documentData.value?.documentFiles.forEach(function (file) {
    if (file.type === "MAIN") {
      mainGuid.value = file.fileGuid;
    }
  });
};

const electronicCopyFor = (originalFileGuid?: string | null) => {
  if (!originalFileGuid) {
    return null;
  }
  return electronicCopyByOriginalGuid.value[originalFileGuid] || null;
};

const mainDocumentInfo = computed(() => ({
  creator: documentData.value?.creatorInfo?.userInfo.fullName,
  description: documentData.value?.documentInfo.description,
  createdAt: documentData.value?.creatorInfo.createdAt,
  fileGuid: electronicCopyFor(mainGuid.value)?.electronicFileGuid || mainGuid.value,
  filename:
    electronicCopyFor(mainGuid.value)?.fileName ||
    (mainFileInfo.value ? buildDisplayFileName(mainFileInfo.value) : ""),
  fileType: electronicCopyFor(mainGuid.value)
    ? "application/pdf"
    : mainFileInfo.value?.type,
  fileExt: electronicCopyFor(mainGuid.value) ? ".pdf" : mainFileInfo.value?.ext,
  isElectronicCopy: Boolean(electronicCopyFor(mainGuid.value)),
}));

const documentGuid = computed(() => {
  return documentData.value?.guid;
});

const attachments = ref([]);

const processes = computed(() =>
  documentData.value?.documentProcesses
    .slice()
    .sort((prev, next) => prev.id - next.id)
);

const currentTab = ref(t("document.tab.requisites"));

const tabsA = computed(() => [
  t("document.tab.requisites"),
  t("document.tab.content"),
  ...(attachments.value.length ? [t("document.tab.attachments")] : []),
  t("document.tab.processes"),
  t("document.tab.history"),
  t("document.tab.accessRights"),
  ...(linkedDocuments.value.length ? [t("document.tab.linkedDocuments")] : []),
  ...(hasEnbekPermission.value ? [t("enbek.tab")] : []),
]);
const linkedDocuments = computed(() =>
  (documentData.value?.linkedDocuments || []).map((linkedDocument: any) => ({
    id: linkedDocument.id,
    name:
      linkedDocument.documentInfo?.title ||
      linkedDocument.documentInfo?.regNumber ||
      `Document #${linkedDocument.id}`,
  }))
);

const documentAccesses = ref<Array<DocumentAccess>>([]);
const documentId = ref<string | null>(null);
const documentStatus = ref<string | null>(null);

const tabs = reactive<Record<string, { component: any; props: any }>>({
  [t("document.tab.requisites")]: {
    component: InfoList,
    props: {
      data: documentDetails,
    },
  },
  [t("document.tab.content")]: {
    component: MainDocumentTable,
    props: {
      file: mainDocumentInfo,
      documentId,
    },
  },
  [t("document.tab.attachments")]: {
    component: AttachmentTable,
    props: {
      files: attachments,
      documentId,
    },
  },
  [t("document.tab.processes")]: {
    component: ProcessHistory,
    props: {
      documentId,
      processes,
      documentStatus,
      documentFiles,
    },
  },
  [t("document.tab.history")]: {
    component: DocumentHistory,
    props: {
      documentGuid: documentGuid,
    },
  },
  [t("document.tab.accessRights")]: {
    component: DocumentAccessTable,
    props: {
      documentId: documentId,
      documentStatus: documentStatus,
      documentAccesses: documentAccesses,
    },
  },
  [t("document.tab.linkedDocuments")]: {
    component: LinkedDocuments,
    props: {
      linkedDocuments,
    },
  },
  [t("enbek.tab")]: {
    component: EnbekDocumentPanel,
    props: {
      documentId,
    },
  },
});

const createLinkedDocument = async () => {
  await router.push({
    name: "DocumentCreate",
    params: { linkedDocumentId: documentData.value?.id },
  });
};

// Download Archive
const archiveFileGuid = ref<string | null>(null);
const archiveGenerate = async () => {
  await DocumentDataService.generateArchive(documentData.value.id)
    .then((result) => {
      console.log(result);
      archiveFileGuid.value = result.data.data.fileGuid;
    })
    .catch((error) => {
      console.log(error);
    });
};
const archiveDownload = async () => {
  await DocumentDataService.downloadDocument(archiveFileGuid.value, null)
    .then((response) => {
      const blob = new Blob([response.data], {
        type: "application/zip",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "archive";
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(console.error);
};

const electronicCopiesLoading = ref(false);
const downloadElectronicCopies = async () => {
  if (!documentData.value?.id || electronicCopiesLoading.value) {
    return;
  }
  electronicCopiesLoading.value = true;
  try {
    const result = await DocumentDataService.downloadElectronicCopies(
      documentData.value.id
    );
    const fileGuid = result?.data?.data?.fileGuid;
    if (!fileGuid) {
      throw new Error("Electronic copies archive is empty");
    }
    const response = await DocumentDataService.downloadDocument(fileGuid, null);
    const blob = new Blob([response.data], {
      type: "application/zip",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "electronic-copies.zip";
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
  } finally {
    electronicCopiesLoading.value = false;
  }
};

let documentLoadSequence = 0;

const loadElectronicCopyFiles = async (documentId: string) => {
  electronicCopyByOriginalGuid.value = {};
  try {
    const response = await DocumentDataService.getElectronicCopyFiles(documentId);
    const files: ElectronicCopyFile[] = response?.data?.data || [];
    electronicCopyByOriginalGuid.value = files.reduce<
      Record<string, ElectronicCopyFile>
    >((result, file) => {
      if (file.originalFileGuid && file.electronicFileGuid) {
        result[file.originalFileGuid] = file;
      }
      return result;
    }, {});
  } catch (error) {
    // Electronic copies are available only for successfully signed documents.
    // If they cannot be generated, keep the original files visible.
    electronicCopyByOriginalGuid.value = {};
  }
};

const loadDocumentDetails = async (id: string | string[]) => {
  const loadSequence = ++documentLoadSequence;

  documentData.value = undefined;
  events.value = {};
  mainGuid.value = undefined;
  mainFileInfo.value = undefined;
  attachedDocuments.value = [];
  attachments.value = [];
  electronicCopyByOriginalGuid.value = {};
  documentAccesses.value = [];
  documentId.value = null;
  documentStatus.value = null;
  documentIsSigning.value = false;
  archiveFileGuid.value = null;
  enbekEligibility.value = null;

  const response: ResponseData = await DocumentDataService.get(id);
  if (loadSequence !== documentLoadSequence) return;

  documentData.value = response.data.data.document;
  mainDocumentGuid();
  await loadElectronicCopyFiles(documentData.value.id);
  if (loadSequence !== documentLoadSequence) return;

  documentAccesses.value = documentData.value?.documentAccesses || [];
  documentId.value = documentData.value?.id || null;
  documentStatus.value = documentData.value?.status || null;
  documentIsSigning.value = documentData.value?.isSigning;
  events.value = response.data.data.documentEvents;

  if (hasEnbekPermission.value) {
    try {
      const enbekResponse = await EnbekDataService.eligibility(documentData.value.id);
      if (loadSequence !== documentLoadSequence) return;
      enbekEligibility.value = enbekResponse.data.data;
    } catch {
      enbekEligibility.value = null;
    }
  }

  const fileGuids = response.data.data.document.documentFiles
    .filter((file: DocumentFiles) => {
      if (file.type === "ARCHIVE") {
        archiveFileGuid.value = file.fileGuid;
        return false;
      }
      return true;
    })
    .map((file: DocumentFiles) => {
      return file.fileGuid;
    });
  const filesResponse = await DocumentDataService.getFilesInfo(fileGuids);
  if (loadSequence !== documentLoadSequence) return;

  attachedDocuments.value = filesResponse.data;
  attachedDocuments.value.forEach((item) => {
    if (item.guid === mainGuid.value) {
      mainFileInfo.value = item;
    } else {
      const electronicCopy = electronicCopyFor(item.guid);
      attachments.value.push({
        creator: documentData.value?.creatorInfo.userInfo.fullName,
        guid: electronicCopy?.electronicFileGuid || item.guid,
        filename: electronicCopy?.fileName || buildDisplayFileName(item),
        createdAt: item.createdAt,
        fileType: electronicCopy ? "application/pdf" : item.type,
        ext: electronicCopy ? ".pdf" : item.ext,
        isElectronicCopy: Boolean(electronicCopy),
      });
    }
  });

  const info = documentData.value?.documentInfo;
  const hasEsedoRequisites =
    info?.esedoDocumentTypeId != null ||
    info?.esedoQuestionNatureId != null ||
    (info?.counterpartyIds?.length ?? 0) > 0;

  if (
    hasEsedoRequisites &&
    currentUser.value?.systemConfig?.documentConstructor === true
  ) {
    try {
      await loadEsedoDictionaries();
    } catch {
      // ESEDO dictionaries are optional for displaying the document card.
    }
  }
};

const isValidDocumentId = (id: unknown): id is string =>
  typeof id === "string" && id.trim().length > 0;

onMounted(() => {
  const id = route.params.id;
  if (isValidDocumentId(id)) {
    loadDocumentDetails(id);
  }
});

watch(
  () => route.params.id,
  (id, previousId) => {
    if (id !== previousId && isValidDocumentId(id)) {
      loadDocumentDetails(id);
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BackButton, {
      to: { name: 'DocumentList' },
      class: "mb-4"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, _toDisplayString(documentData.value?.documentInfo.title), 1)
      ]),
      (documentData.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([
            {
              title: _ctx.$t('document.edit'),
              show: events.value.canUpdate,
              action: () => {
                updateDocument();
              },
            },
            {
              title: _ctx.$t('document.send'),
              show: events.value.canSendForExecution,
              classes: 'bg-primary-300 text-white hover:bg-primary-600',
              action: () => {
                sendDocumentForExecution();
              },
            },
            {
              title: _ctx.$t('document.return'),
              show: events.value.canReturn,
              action: () => {
                returnDocument();
              },
            },
            {
              title: _ctx.$t('document.delete'),
              show: events.value.canDelete,
              action: () => {
                deleteDocument();
              },
            },
          ], ({ title, show, action, classes }) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: title,
                  onClick: action
                }, [
                  (show === true)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: _normalizeClass(`${
              classes ? classes : 'text-gray-800 hover:bg-gray-100'
            } px-4 py-2 border border-gray-300 rounded-lg`)
                      }, _toDisplayString(title), 3))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_6))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_7, [
              (
            _unref(documentDetails).status === _unref(DocumentStatusesEnum).SUCCESS &&
            !archiveFileGuid.value
          )
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100",
                    onClick: archiveGenerate
                  }, _toDisplayString(_ctx.$t("document.generateArchive")), 1))
                : _createCommentVNode("", true),
              (archiveFileGuid.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100",
                    onClick: archiveDownload
                  }, _toDisplayString(_ctx.$t("document.downloadArchive")), 1))
                : _createCommentVNode("", true),
              (_unref(documentDetails).status === _unref(DocumentStatusesEnum).SUCCESS)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100",
                    disabled: electronicCopiesLoading.value,
                    onClick: downloadElectronicCopies
                  }, _toDisplayString(_ctx.$t("document.downloadElectronicCopies")), 9, _hoisted_8))
                : _createCommentVNode("", true),
              (_unref(canRegisterInEnbek))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 3,
                    class: "px-4 py-2 rounded-lg bg-primary-300 text-white hover:bg-primary-600",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (currentTab.value = _unref(t)('enbek.tab')))
                  }, _toDisplayString(_ctx.$t("enbek.register")), 1))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      (documentData.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(_unref(TabButtons), {
              options: _unref(tabsA),
              modelValue: currentTab.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentTab).value = $event)),
              class: "w-full text-md"
            }, null, 8, ["options", "modelValue"]),
            (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(tabs)[currentTab.value].component), _normalizeProps(_guardReactiveProps(_unref(tabs)[currentTab.value].props)), null, 16))
          ], 64))
        : _createCommentVNode("", true)
    ])
  ], 64))
}
}

})